import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { CoinSymbol } from '@asia/shared';
import { useNav } from '../../store/nav';
import { useActiveWallet } from '../../store/wallets';
import { useSession } from '../../hooks/useSession';
import { useBalances, useTxs } from '../../hooks/useChain';
import { LoadMore } from './LoadMore';
import { haptic } from '../../hooks/useTelegram';
import { mockCoins, mockTransactions } from '../../lib/mock';
import { fetchPrice, fetchChart } from '../../lib/prices';
import { groupInt, fmtUsd, fmtPrice, fmtPct, priceDecimals, fmtDateHeading } from '../../lib/format';
import { CoinIcon } from '../CoinIcon';
import { FigIcon, type FigIconName } from '../FigIcon';
import { ChevronDown } from '../icons';
import { TransactionRow } from './TransactionRow';
import { TransactionDetailSheet } from './TransactionDetailSheet';
import { PriceChart } from './PriceChart';

const RANGES: { key: string; labelKey: string }[] = [
  { key: 'all', labelKey: 'coin.rangeAll' },
  { key: '1y', labelKey: 'coin.range1y' },
  { key: '3m', labelKey: 'coin.range3m' },
  { key: '1m', labelKey: 'coin.range1m' },
  { key: '1w', labelKey: 'coin.range1w' },
  { key: '1d', labelKey: 'coin.range1d' },
];

export function CoinInfoScreen({ coin }: { coin: CoinSymbol }) {
  const { t } = useTranslation();
  const push = useNav((s) => s.push);
  const c = mockCoins.find((x) => x.symbol === coin) ?? mockCoins[0]!;
  const { token } = useSession();
  const wallet = useActiveWallet();
  const balances = useBalances(wallet?.address).data;
  const { data: txs, hasNextPage, isFetchingNextPage, fetchNextPage } = useTxs(wallet?.address);
  const [range, setRange] = useState('1d');
  const [hover, setHover] = useState<{ priceUsd: number; date: string } | null>(null);
  const [chartOpen, setChartOpen] = useState(true);
  const [detailTx, setDetailTx] = useState<(typeof mockTransactions)[number] | null>(null);

  // Live price (Binance → CoinGecko). No mock fallback: while loading the price
  // shows a placeholder rather than a stale/wrong value.
  const priceQ = useQuery({
    queryKey: ['price', coin],
    queryFn: () => fetchPrice(coin),
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });
  const chartQ = useQuery({
    queryKey: ['chart', coin, range],
    queryFn: () => fetchChart(coin, range),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const priceUsd = priceQ.data?.usd;
  const changePct = priceQ.data?.change24h ?? 0;
  // Real on-chain balance for this coin in a real client; mock baseline in dev.
  const balance = token ? (coin === 'USDT' ? balances?.usdt : balances?.trx) ?? 0 : c.balance;
  const usdValue = priceUsd != null ? balance * priceUsd : null;
  const up = changePct >= 0;
  const dash = '···';

  const actions: { key: string; icon: FigIconName; label: string; onClick: () => void }[] = [
    { key: 'receive', icon: 'receive', label: t('wallet.receive'), onClick: () => push({ name: 'receive' }) },
    { key: 'send', icon: 'send', label: t('wallet.send'), onClick: () => push({ name: 'send', coin }) },
    { key: 'buy', icon: 'buy', label: t('wallet.buy'), onClick: () => push({ name: 'buy' }) },
  ];

  const source = token ? txs ?? [] : mockTransactions;
  const coinTxs = source
    .filter((tx) => tx.coin === coin)
    .map((tx) => ({ ...tx, amountUsd: tx.amountUsd || Math.abs(tx.amount) * (priceUsd ?? (coin === 'USDT' ? 1 : 0)) }));
  const byDay = coinTxs.reduce<Record<string, typeof coinTxs>>((acc, tx) => {
    (acc[fmtDateHeading(tx.date)] ??= []).push(tx);
    return acc;
  }, {});

  return (
    <div className="push-enter flex min-h-full flex-col bg-surface pb-32">
      <div className="flex flex-col gap-6 px-4 pt-8">
        {/* coin identity */}
        <div className="flex flex-col items-center gap-1">
          <CoinIcon symbol={coin} size={80} />
          <p className="mt-3 text-[24px] font-medium text-ink">
            {groupInt(balance)} {c.name.toUpperCase()}
          </p>
          <p className="text-[15px] text-muted">{usdValue != null ? fmtUsd(usdValue) : dash}</p>
        </div>

        {/* action row */}
        <div className="flex gap-3">
          {actions.map(({ key, icon, label, onClick }) => (
            <button
              key={key}
              onClick={() => {
                haptic();
                onClick();
              }}
              className="flex flex-1 flex-col items-center gap-2 rounded-[18px] bg-card py-3 shadow-card transition-transform active:scale-95"
            >
              <FigIcon name={icon} size={26} className="text-accent" />
              <span className="text-[12px] font-medium text-ink">{label}</span>
            </button>
          ))}
        </div>

        {/* price chart card */}
        <div className="flex flex-col rounded-[28px] bg-card p-4">
          <button
            onClick={() => {
              haptic();
              setChartOpen((o) => !o);
            }}
            aria-expanded={chartOpen}
            className="flex items-center justify-between active:opacity-70"
          >
            <div className="flex w-[140px] flex-col items-start gap-[2px]">
              <p className="text-[13px] leading-[1.44] text-muted">{t('coin.price')}</p>
              <div className="flex items-baseline gap-[6px]">
                <span className="text-[18px] font-semibold text-ink">
                  {hover?.priceUsd != null
                    ? fmtPrice(hover.priceUsd, priceDecimals(coin))
                    : priceUsd != null
                      ? fmtPrice(priceUsd, priceDecimals(coin))
                      : dash}
                </span>
                <span className={`text-[13px] font-medium ${up ? 'text-positive' : 'text-negative'}`}>
                  {hover ? hover.date : priceQ.data != null ? fmtPct(changePct) : ''}
                </span>
              </div>
            </div>
            <ChevronDown
              width={16}
              height={16}
              className={`text-muted transition-transform duration-300 ${chartOpen ? 'rotate-180' : 'rotate-0'}`}
            />
          </button>

          {/* Smooth collapse: animate the row height (0fr↔1fr) rather than
              mounting/unmounting. The clipped region is pulled to the card edges
              (-mx-4) so the full-bleed chart isn't sliced by overflow-hidden;
              the range tabs get their padding back with px-4. */}
          <div
            className="grid transition-[grid-template-rows] duration-300 ease-out"
            style={{ gridTemplateRows: chartOpen ? '1fr' : '0fr' }}
          >
            <div className="-mx-4 overflow-hidden">
              <div className="flex flex-col gap-7 pt-7">
                <PriceChart data={chartQ.data ?? []} loading={chartQ.isLoading} onHover={setHover} />

                {/* range tabs */}
                <div className="mx-4 flex items-center rounded-full bg-surface p-[3px]">
                  {RANGES.map((r) => {
                    const on = r.key === range;
                    return (
                      <button
                        key={r.key}
                        onClick={() => {
                          haptic();
                          setRange(r.key);
                        }}
                        className={`flex flex-1 items-center justify-center rounded-full px-[10px] py-1 text-[13px] font-medium text-ink ${on ? 'bg-card' : ''}`}
                      >
                        {t(r.labelKey)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* transactions for this coin */}
        {coinTxs.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-card bg-card px-6 py-10 shadow-card">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-surface">
              <FigIcon name="pfReceipt" size={26} className="text-muted" />
            </div>
            <div className="flex flex-col items-center gap-1">
              <p className="text-[15px] font-semibold text-ink">{t('coin.noTx')}</p>
              <p className="max-w-[240px] text-center text-[13px] leading-[1.4] text-muted">
                {t('coin.noTxHint')}
              </p>
            </div>
          </div>
        ) : (
          Object.entries(byDay).map(([day, txs]) => (
            <section key={day}>
              <h3 className="px-1 text-[15px] font-semibold text-muted">{day}</h3>
              <div className="mt-2 divide-y divide-border overflow-hidden rounded-card bg-card shadow-card">
                {txs.map((tx) => (
                  <TransactionRow key={tx.id} tx={tx} onClick={() => setDetailTx(tx)} />
                ))}
              </div>
            </section>
          ))
        )}
        <LoadMore hasMore={!!hasNextPage} loading={isFetchingNextPage} onLoadMore={fetchNextPage} />
      </div>

      {detailTx && <TransactionDetailSheet tx={detailTx} onClose={() => setDetailTx(null)} />}
    </div>
  );
}
